import { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import cancelIcon from '../../../assets/images/faqImage/cancel-bl.svg';
import backgroundImage from '../../../assets/images/ndc-bg.png';
import TrapFocus from '@mui/material/Unstable_TrapFocus';

const AlertPopUp = ({
  open,
  handleClose,
  header,
  ariaLabel,
  body,
  buttonContent,
  language
}) => {
  const modalRef: any = useRef(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Mobile devices
  const isTablet = useMediaQuery(theme.breakpoints.down(830)); // Tablet devices

  const [timer, setTimer] = useState(true);

  const handleCloseAndNavigate = () => {
    handleClose();
    navigate(`/${language}/`);
  };

  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      // Freeze background scrolling
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      document.body.style.width = '100%';
    } else {
      // Re-enable background scrolling
      const scrollY = Math.abs(parseInt(document.body.style.top || '0', 10));
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY); // Restore the scroll position
    }

    return () => {
      // Cleanup: Re-enable scrolling on unmount
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
    };
  }, [open]);

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      setTimer(false);
    }, 10);

    return clearTimeout(timeOutId);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        handleCloseAndNavigate();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
        handleCloseAndNavigate();
      }}
      // aria-labelledby='alert-popup-title'
      aria-describedby="alert-popup-description"
    >
      <TrapFocus open={open} initialFocusRef={modalRef}>
        <Box
          ref={modalRef}
          aria-modal="true"
          role="dialog"
          tabIndex={-1}
          sx={{
            position: 'absolute',
            top: isMobile || isTablet ? '12%' : '55%',
            left: '50%',
            transform:
              isMobile || isTablet
                ? 'translateX(-50%)'
                : 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            // width: isMobile ? "90%" : isTablet ? "70%" : "55rem", // Adjust width for mobile and tablet
            borderRadius: '1rem',
            display: 'flex',
            flexDirection: 'column',
            outline: 0,
            height: isMobile ? '356px' : isTablet ? '392px' : '480px',
            width: isMobile ? '344px' : isTablet ? '720px' : '880px'
          }}
        >
          <Box
            sx={{
              background: `url(${backgroundImage})`,
              height: isMobile ? '120px' : isTablet ? '188px' : '140px', // Adjust height for mobile
              borderRadius: '1rem 1rem 0 0',
              backgroundRepeat: 'round',
              px: isMobile ? '1.5rem' : '2.5rem', // Adjust padding for mobile
              py: isMobile ? '1.5rem' : '',
              justifyContent: 'space-between',
              alignItems: 'center',
              display: 'flex'
            }}
          >
            <Typography
              component={'h1'}
              id="alert-popup-title"
              aria-label={ariaLabel}
              sx={{
                fontSize: isMobile || isTablet ? '1.7rem' : '2.2rem', // Adjust font size for mobile
                fontWeight: 700,
                lineHeight: isMobile ? '2rem' : '3rem' // Adjust line height for mobile
              }}
            >
              {header}
            </Typography>
            <IconButton
              onClick={handleCloseAndNavigate}
              sx={{ height: '2.5rem', width: '2.5rem' }}
              aria-label="Close"
            >
              <img src={cancelIcon} alt="close icon" />
            </IconButton>
          </Box>
          <Box
            aria-hidden={timer}
            sx={{
              height: '100%',
              position: 'relative',
              overflowY: 'auto',
              p: isMobile ? '1.5rem' : '2.5rem', // Adjust padding for mobile
              '&::-webkit-scrollbar': {
                display: 'none' // Hide scrollbar for WebKit browsers
              },
              // paddingBottom:"1.5rem",
              '-ms-overflow-style': 'none', // Hide scrollbar for Internet Explorer
              'scrollbar-width': 'none', // Hide scrollbar for Firefox
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1
              }}
            >
              <Typography
                // variant="h3"
                sx={{
                  fontSize: { xs: '14px', sm: '16px', md: '18px' },
                  lineHeight: { xs: '26px', sm: '28px', md: '32px' },
                  fontWeight: 500,
                  textAlign: 'left',
                  px: 1,
                  display: 'inline'
                }}
              >
                {body}
              </Typography>
            </div>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
                pt: 6
              }}
            >
              <Button
                variant="primary"
                size="md"
                onClick={handleCloseAndNavigate}
              >
                {buttonContent}
              </Button>
            </Box>
          </Box>
        </Box>
      </TrapFocus>
    </Modal>
  );
};

export default AlertPopUp;
